"use client";

import { useEffect } from "react";
import useSWR, { type SWRConfiguration } from "swr";
import { useUser } from "../providers/user-provider";

export interface CachedResponse<T> {
  data: T | undefined;
  isLoading: boolean;
  error?: Error;
  mutate: () => void;
  isValidating: boolean;
}

interface UseCachedRequestParams<T> {
  url: string | null;
  parser?: (data: object) => T;
  swrOptions?: SWRConfiguration;
  anonymousRequest?: boolean;
}

export function useCachedRequest<T>({
  url,
  parser,
  swrOptions,
  anonymousRequest,
}: UseCachedRequestParams<T>): CachedResponse<T | null> {
  const { buildRequestHeaders, isReady, logout } = useUser();
  const fetcher = async (): Promise<{
    data: T | null;
    errorCode?: string;
    error?: Error;
  }> => {
    if (url === null) {
      throw new Error("URL is null");
    }

    const headers = anonymousRequest
      ? {
          "content-type": "application/json",
        }
      : await buildRequestHeaders();
    const response = await fetch(url, {
      method: "GET",
      headers,
    });
    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        if (data.error.code === "invalid_or_expired_jwt") {
          return { data: null, errorCode: data.error.code };
        }
        if (!anonymousRequest) {
          logout();
        }
        return { data: null, errorCode: data.error.code };
      }
      throw new Error(data.error);
    }

    if (parser) {
      try {
        return { data: parser(data) };
      } catch (error) {
        return { data: null, error: error as Error };
      }
    }

    return { data };
  };

  const { data, error, isLoading, mutate, isValidating } = useSWR(
    (anonymousRequest || isReady) && url ? url : null,
    fetcher,
    { ...swrOptions },
  );
  useEffect(() => {
    if (data?.errorCode === "invalid_or_expired_jwt") {
      // do nothing, jwt will be refreshed
    }
    if (data?.errorCode === "organization_forbidden") {
      window.location.href = "/organization-select";
    }
  }, [data, error]);

  return {
    data: data?.data || null,
    isLoading: isLoading,
    error: error || data?.error,
    mutate,
    isValidating,
  };
}
